// src/components/artworks/forms/SignatureSection.jsx
import React, { useState } from "react";
import { supabase } from "../../../lib/supabaseClient";
import { useTranslation } from "react-i18next";

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {children}
    </div>
  );
}

function safeJsonObject(value) {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

function safeArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function normalizeBoolean(value) {
  if (value === true || value === false) return value;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (["true", "1", "yes", "oui", "signed", "signé", "signee"].includes(v)) return true;
    if (["false", "0", "no", "non", "unsigned", "non signé", "non signee"].includes(v)) return false;
  }
  return false;
}

function isCertificateDocument(doc = {}) {
  const text = [doc.type, doc.category, doc.document_type, doc.name, doc.title, doc.filename]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  return (
    text.includes("certificate") ||
    text.includes("certificat") ||
    text.includes("authenticite") ||
    text.includes("authenticity")
  );
}

export default function SignatureSection(props) {
  const { t } = useTranslation();
  const [applyingLot, setApplyingLot] = useState(false);
  const [lotApplyMessage, setLotApplyMessage] = useState("");

  // Compatibilité :
  // 1) <SignatureSection formData={...} handleInputChange={...} />
  // 2) <SignatureSection data={...} onChange={...} />
  const data = props.formData || props.data || {};
  const readOnly = Boolean(props.readOnly);
  const signatureInfo = safeJsonObject(data.signature_info);
  const documents = safeArray(data.documents);
  const certificateDoc = documents.find(isCertificateDocument);

  const firstText = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      const text = String(value);
      if (text.trim()) return text;
    }
    return "";
  };

  const isSigned = normalizeBoolean(data.signature ?? signatureInfo.signed);
  const signatureValue = firstText(
    data.signature_desc,
    data.signatureDesc,
    signatureInfo.description
  );

  const notesValue = firstText(signatureInfo.notes);

  const hasCertificate = Boolean(
    certificateDoc ||
      data.certificate_auth ||
      firstText(data.certificate_authenticity, data.certificateAuthenticity)
  );

  const certificateName = firstText(
    certificateDoc?.name,
    certificateDoc?.title,
    certificateDoc?.filename,
    hasCertificate ? t("signature.certificateFallbackName") : ""
  );

  const updateField = (field, value, type = "text") => {
    if (readOnly) return;

    if (typeof props.handleInputChange === "function") {
      props.handleInputChange({
        target: {
          name: field,
          value,
          type,
          checked: type === "checkbox" ? Boolean(value) : undefined,
        },
      });
      return;
    }

    if (typeof props.onChange === "function") {
      props.onChange({ ...data, [field]: value });
    }
  };

  const updateMany = (patch) => {
    if (readOnly) return;

    if (typeof props.handleInputChange === "function") {
      Object.entries(patch).forEach(([field, value]) => {
        updateField(field, value, typeof value === "boolean" ? "checkbox" : "text");
      });
      return;
    }

    if (typeof props.onChange === "function") {
      props.onChange({ ...data, ...patch });
    }
  };

  const handleSignatureBooleanChange = (e) => {
    const checked = e.target.checked;
    updateMany({
      signature: checked,
      signature_info: {
        ...signatureInfo,
        signed: checked,
      },
    });
  };

  const handleSignatureDescriptionChange = (e) => {
    const value = e.target.value;
    updateMany({
      signature_desc: value,
      signature: value.trim().length > 0 ? true : isSigned,
      signature_info: {
        ...signatureInfo,
        description: value,
        signed: value.trim().length > 0 ? true : isSigned,
      },
    });
  };

  const handleNotesChange = (e) => {
    const value = e.target.value;
    updateField("signature_info", {
      ...signatureInfo,
      notes: value,
    });
  };

  const handleApplySignatureToLot = async () => {
    if (readOnly || !data.lot_id || applyingLot) return;

    const confirmed = window.confirm(
      t("signature.applyToLotConfirm")
    );
    if (!confirmed) return;

    setApplyingLot(true);
    setLotApplyMessage("");

    const nextSignatureInfo = {
      ...signatureInfo,
      signed: isSigned,
      description: signatureValue,
    };

    let query = supabase
      .from("artworks")
      .update({
        signature: isSigned,
        signature_desc: signatureValue.trim() || null,
        signature_info: nextSignatureInfo,
        updated_at: new Date().toISOString(),
      })
      .eq("lot_id", data.lot_id)
      .select("id");

    if (data.client_id) query = query.eq("client_id", data.client_id);

    const { data: updatedRows, error } = await query;

    setApplyingLot(false);

    if (error) {
      console.error("[SignatureSection] apply signature to lot error:", error);
      setLotApplyMessage(error.message || t("signature.applyToLotError"));
      return;
    }

    const count = updatedRows?.length || 0;
    setLotApplyMessage(
      t("signature.applyToLotSuccess", { count })
    );

    if (typeof props.onLotSignatureUpdated === "function") {
      props.onLotSignatureUpdated(updatedRows || []);
    }
  };

  return (
    <section className="mt-10 border border-gray-200 rounded-2xl bg-white">
      <div className="px-6 pt-6 pb-4 border-b border-gray-200">
        <h3 className="text-xl font-semibold">
          {t("signature.title")}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {t("signature.description")}
        </p>
      </div>

      <div className="px-6 py-6 grid gap-6 md:grid-cols-2">
        <Field label={t("signature.signatureLabel")}>
          <label className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-3 text-sm">
            <input
              type="checkbox"
              name="signature"
              checked={isSigned}
              onChange={handleSignatureBooleanChange}
              disabled={readOnly}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="font-medium text-gray-800">
              {t("signature.signatureBooleanLabel")}
            </span>
          </label>

          <input
            type="text"
            className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={signatureValue}
            onChange={handleSignatureDescriptionChange}
            placeholder={t("signature.signaturePlaceholder")}
            readOnly={readOnly}
          />

          {data.lot_id && !readOnly && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs text-amber-800">
                {t("signature.lotHelp")}
              </p>
              <button
                type="button"
                onClick={handleApplySignatureToLot}
                disabled={applyingLot}
                className="mt-2 rounded-lg bg-black px-3 py-2 text-xs font-medium text-white disabled:opacity-40"
              >
                {applyingLot
                  ? t("signature.applyToLotLoading")
                  : t("signature.applyToLot")}
              </button>
              {lotApplyMessage && (
                <p className="mt-2 text-xs text-amber-900">{lotApplyMessage}</p>
              )}
            </div>
          )}
        </Field>

        <Field label={t("signature.certificateLabel")}>
          <div
            className={`w-full rounded-lg border px-3 py-2 text-sm ${
              hasCertificate
                ? "border-green-200 bg-green-50 text-green-800"
                : "border-gray-200 bg-gray-50 text-gray-500"
            }`}
          >
            {hasCertificate ? (
              <>
                <div className="font-medium">
                  {t("signature.certificatePresent")}
                </div>
                <div className="mt-1 text-xs">
                  {t("signature.certificateInDocuments")}{" "}
                  <span className="font-medium">{certificateName}</span>
                </div>
              </>
            ) : (
              t("signature.noCertificate")
            )}
          </div>
        </Field>
      </div>

      <div className="px-6 pb-6">
        <Field label={t("signature.notesLabel")}>
          <textarea
            rows={3}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={notesValue}
            onChange={handleNotesChange}
            placeholder={t("signature.notesPlaceholder")}
            readOnly={readOnly}
          />
        </Field>
      </div>
    </section>
  );
}
